/**
 * Helpers for the "Email me a receipt" form.
 *
 * The recipient NAME comes from the signed-in profile. It used to be sent as an
 * empty string, which the receipt API rejects (name >= 2 chars).
 */

export const MIN_RECIPIENT_NAME_LENGTH = 2;
export const MAX_RECIPIENT_NAME_LENGTH = 120;

/** "First Last", trimmed, single-token names allowed; '' when the profile has none. */
export function deriveRecipientName(user) {
  if (!user) return '';
  return [user.firstName, user.lastName]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(' ');
}

export function isValidRecipientName(name) {
  const n = typeof name === 'string' ? name.trim() : '';
  return n.length >= MIN_RECIPIENT_NAME_LENGTH && n.length <= MAX_RECIPIENT_NAME_LENGTH;
}

/**
 * Mirrors the server's `isSendableEmail` (services/notifications/receiptRecipient.js)
 * so the driver is told about a bad address before the request, not after.
 */
export function isSendableEmail(value) {
  if (typeof value !== 'string') return false;
  const s = value.trim();
  if (s.length === 0 || s.length > 254 || /\s/.test(s)) return false;
  const at = s.lastIndexOf('@');
  if (at <= 0 || at === s.length - 1) return false;
  const local = s.slice(0, at);
  const domain = s.slice(at + 1);
  if (local.length > 64) return false;
  if (domain.indexOf('.') <= 0) return false;
  if (domain.startsWith('.') || domain.endsWith('.') || domain.includes('..')) return false;
  if (/[^A-Za-z0-9.+_%-]/.test(local)) return false;
  if (/[^A-Za-z0-9.-]/.test(domain)) return false;
  return true;
}

export function emailFieldError(value) {
  const s = typeof value === 'string' ? value.trim() : '';
  if (!s) return 'Enter an email address.';
  if (!isSendableEmail(s)) return 'Enter a valid email address, like name@example.com.';
  return null;
}

export function nameFieldError(value) {
  return isValidRecipientName(value)
    ? null
    : `Enter your name (at least ${MIN_RECIPIENT_NAME_LENGTH} characters).`;
}
